"use client";

import { useEffect, useRef } from "react";

/**
 * Universal, provider-neutral in-workspace navigation.
 *
 * A rendered functional state is a navigation frame. A Back request restores
 * the preceding actual frame BEFORE enterprise-tab history can run. No browser
 * history entry or backend mutation is created for an in-page state change.
 *
 * Call once per active workspace with only navigation state (never secrets,
 * input drafts, tokens or asynchronously refreshed provider data). Consumers
 * may handle their highest-priority modal first using the same cancelable
 * runyi:request-back event.
 */
export function useRunyiBackTrail<T>(
  frame: T,
  restore: (previous: T) => void,
  active = true,
) {
  const key = JSON.stringify(frame);
  const latestRef = useRef({ frame, key, restore });
  latestRef.current = { frame, key, restore };
  const previousRef = useRef<{ frame: T; key: string } | null>(null);
  const trailRef = useRef<Array<{ frame: T; key: string; scrollY: number }>>([]);
  const restoringRef = useRef<string | null>(null);

  useEffect(() => {
    if (!active) return;
    const before = previousRef.current;
    if (restoringRef.current === key) {
      restoringRef.current = null;
      previousRef.current = { frame, key };
      return;
    }
    if (before && before.key !== key) {
      trailRef.current.push({
        ...before,
        scrollY: typeof window !== "undefined" ? window.scrollY : 0,
      });
      if (trailRef.current.length > 50) trailRef.current.shift();
    }
    previousRef.current = { frame, key };
  }, [active, frame, key]);

  useEffect(() => {
    if (!active) {
      trailRef.current = [];
      previousRef.current = null;
      restoringRef.current = null;
      return;
    }
    const onBack = (event: Event) => {
      if (event.defaultPrevented) return;
      const current = latestRef.current;
      let previous = trailRef.current.pop();
      while (previous && previous.key === current.key) previous = trailRef.current.pop();
      if (!previous) return;
      event.preventDefault();
      restoringRef.current = previous.key;
      previousRef.current = { frame: previous.frame, key: previous.key };
      current.restore(previous.frame);
      const scrollY = previous.scrollY;
      window.requestAnimationFrame(() => window.scrollTo({ top: scrollY, behavior: "auto" }));
    };
    window.addEventListener("runyi:request-back", onBack);
    return () => window.removeEventListener("runyi:request-back", onBack);
  }, [active]);
}
